import React, {FC, useState, useEffect} from 'react';
import {CustomDatePicker} from './CustomDatePicker';
import dayjs, {Dayjs} from 'dayjs';
import utc from 'dayjs/plugin/utc';
import styled from 'styled-components';
import {Input} from 'antd';
import {LocalTimeDisplay} from '../../businessCommon/localTimeDisplay/LocalTimeDisplay';

dayjs.extend(utc);

const DateAndTimeWrapper = styled.div<{ $dateOnFullRow?: boolean }>`
  display: flex;
  flex-wrap: ${props => props.$dateOnFullRow ? 'wrap' : 'nowrap'};
  gap: 8px;
  align-items: center;
  width: 100%;
  min-width: 0;
  overflow: visible;
  
  /* Когда dateOnFullRow активен, уменьшаем вертикальный gap между датой и временем/таймзоной */
  ${props => props.$dateOnFullRow && `
    row-gap: 4px;
    column-gap: 8px;
  `}
`;

const DatePickerWrapper = styled.div<{ $dateOnFullRow?: boolean }>`
  width: ${props => props.$dateOnFullRow ? '100%' : '100%'};
  min-width: 120px;
  flex: ${props => props.$dateOnFullRow ? '0 0 100%' : '1'};
  
  > div {
    width: 100%;
    min-width: 0;
  }
  
  .ant-picker {
    min-width: 80px;
  }
`;

// Невидимый спейсер для выталкивания времени и таймзоны на следующий ряд
const Spacer = styled.div`
  flex: 1 1 100%;
  min-width: 0;
  height: 0;
`;

const TimeInputWrapper = styled.div`
  min-width: 70px;
  width: 70px;
  flex-shrink: 0;
`;

const TimeInput = styled(Input)`
  height: 48px;
  border-radius: 6px;
  border: 1px solid #d9d9d9;
  font-size: 14px;
  text-align: center;
  
  &:hover {
    border-color: #40a9ff;
  }
  
  &:focus {
    border-color: #40a9ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
  
  &::placeholder {
    color: #bfbfbf;
  }
`;

const DateAndTimeContainer = styled.div<{ $containerHeight?: string | number }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: ${props => props.$containerHeight !== undefined ? (typeof props.$containerHeight === 'string' ? props.$containerHeight : `${props.$containerHeight}px`) : '50px'};
`;

const LocalTimeWrapper = styled.div`
  margin-top: 4px;
  display: flex;
  justify-content: flex-end;
  min-height: 18px;
`;

interface IDateAndTimePickerProps {
  value?: Dayjs | null;
  onChange?: (date: Dayjs | null, dateString: string) => void;
  placeholder?: string;
  disabledDate?: (current: Dayjs) => boolean;
  inputReadOnly?: boolean;
  placement?: "bottomLeft" | "bottomRight" | "topLeft" | "topRight";
  getPopupContainer?: (trigger: HTMLElement) => HTMLElement;
  minTime?: Dayjs | null;
  maxTime?: Dayjs | null;
  dateOnFullRow?: boolean;
  airportOffset?: number | null;
  containerHeight?: string | number;
  [key: string]: any;
}

export const DateAndTimePicker: FC<IDateAndTimePickerProps> = ({
  value,
  onChange,
  placeholder,
  disabledDate,
  inputReadOnly = true,
  placement = "bottomLeft",
  getPopupContainer,
  minTime,
  maxTime,
  dateOnFullRow = false,
  airportOffset,
  containerHeight,
  ...props
}) => {
  const [dateValue, setDateValue] = useState<Dayjs | null>(value || null);
  const [timeValue, setTimeValue] = useState<string>('');
  const [, setIsTimeEntered] = useState<boolean>(false);

  // Инициализация времени из value
  useEffect(() => {
    if (value) {
      setDateValue(value);
      const timeStr = value.format('HH:mm');
      setTimeValue(timeStr);
      setIsTimeEntered(true);
    } else {
      setDateValue(null);
      setTimeValue('');
      setIsTimeEntered(false);
    }
  }, [value]);

  // Вычисляем значение для отображения в DatePicker (только дата, без времени)
  const displayDateValue = dateValue ? dateValue.startOf('day') : null;

  // Валидация времени
  const validateTime = (time: string): boolean => {
    const timeRegex = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/;
    return timeRegex.test(time);
  };

  // Обработка изменения времени
  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const target = e.target;
    const caretPos = target.selectionStart ?? 0;
    
    // Разрешаем только цифры и двоеточие
    if (!/^[0-9:]*$/.test(inputValue)) {
      return;
    }
    
    // Ограничиваем длину
    if (inputValue.length > 5) {
      return;
    }
    
    // Автоматически добавляем или удаляем двоеточие
    let formattedValue = inputValue;
    let newCaretPos = caretPos;
    
    // Если осталось только "HH:" (две цифры и двоеточие), убираем двоеточие
    if (inputValue.length === 3 && /^\d{2}:$/.test(inputValue)) {
      formattedValue = inputValue.slice(0, 2);
      newCaretPos = 2; // Курсор в конце часов
    }
    // Если введено 2 цифры подряд без двоеточия, добавляем двоеточие
    else if (inputValue.length === 2 && !inputValue.includes(':')) {
      formattedValue = inputValue + ':';
      newCaretPos = 3; // Курсор после двоеточия
    }
    // Если введено 3 символа и это "HHH" (три цифры), форматируем как "HH:0"
    else if (inputValue.length === 3 && !inputValue.includes(':')) {
      formattedValue = inputValue.slice(0, 2) + ':' + inputValue.slice(2);
      newCaretPos = 4; // Курсор после двоеточия и первой цифры минут
    }
    // Если введено 4 символа и это "HHHH" (четыре цифры), форматируем как "HH:MM"
    else if (inputValue.length === 4 && !inputValue.includes(':')) {
      formattedValue = inputValue.slice(0, 2) + ':' + inputValue.slice(2);
      newCaretPos = 5; // Курсор в конце
    }
    // Если введена только одна цифра "0", форматируем как "00:00" (только если это первая цифра)
    else if (inputValue.length === 1 && /^0$/.test(inputValue) && caretPos === 1) {
      // Не форматируем сразу, оставляем как есть, чтобы пользователь мог продолжить ввод
      // Форматирование произойдет на blur
    }
    
    setTimeValue(formattedValue);
    
    // Устанавливаем позицию курсора после обновления значения
    if (newCaretPos !== caretPos) {
      requestAnimationFrame(() => {
        try {
          target.setSelectionRange(newCaretPos, newCaretPos);
        } catch {}
      });
    }
    
    // Если время полностью стерто, просто сбрасываем флаг
    if (!formattedValue || formattedValue.trim() === '') {
      setIsTimeEntered(false);
      // Не вызываем onChange, чтобы пользователь мог свободно стирать время
      // Валидация проверит наличие времени при отправке формы
      return;
    }
    
    // Если время валидно, проверяем ограничения и обновляем общее значение
    if (validateTime(formattedValue) && dateValue) {
      const [hours, minutes] = formattedValue.split(':');
      const newDateTime = dateValue.hour(parseInt(hours)).minute(parseInt(minutes));
      
      // Проверяем ограничения по времени
      let isValid = true;
      
      if (minTime && dateValue.isSame(minTime, 'day')) {
        // Если дата совпадает с minTime, проверяем, что время >= minTime
        if (newDateTime.isBefore(minTime)) {
          isValid = false;
          // Если время раньше минимального, сбрасываем к минимальному
          const minDateTime = minTime;
          setTimeValue(minDateTime.format('HH:mm'));
          setDateValue(minDateTime);
          if (onChange) {
            onChange(minDateTime, minDateTime.format('YYYY-MM-DD HH:mm'));
          }
          return;
        }
      }
      
      if (maxTime && dateValue.isSame(maxTime, 'day')) {
        // Если дата совпадает с maxTime, проверяем, что время <= maxTime
        if (newDateTime.isAfter(maxTime)) {
          isValid = false;
          // Если время позже максимального, сбрасываем к максимальному
          const maxDateTime = maxTime;
          setTimeValue(maxDateTime.format('HH:mm'));
          setDateValue(maxDateTime);
          if (onChange) {
            onChange(maxDateTime, maxDateTime.format('YYYY-MM-DD HH:mm'));
          }
          return;
        }
      }
      
      if (isValid) {
        setDateValue(newDateTime);
        setIsTimeEntered(true);
        if (onChange) {
          onChange(newDateTime, newDateTime.format('YYYY-MM-DD HH:mm'));
        }
      }
    }
  };

  // Автопереход назад к часам при удалении минут
  const handleTimeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Backspace') return;
    const target = e.currentTarget;
    const caretPos = target.selectionStart ?? 0;
    const val = target.value;

    // Если курсор сразу после двоеточия и минут уже нет (формат "HH:")
    // не удаляем двоеточие, а переносим курсор к часам
    if (caretPos === 3 && /^\d{1,2}:$/.test(val)) {
      e.preventDefault();
      // переносим каретку в конец часов (позиция 2)
      requestAnimationFrame(() => {
        try {
          target.setSelectionRange(2, 2);
        } catch {}
      });
    }
    // Если после backspace останется только "HH:" (две цифры и двоеточие), удаляем двоеточие
    else if (caretPos === 3 && /^\d{1,2}:\d?$/.test(val)) {
      // Если удаляем символ после двоеточия и останется только "HH:"
      const newValue = val.slice(0, caretPos - 1) + val.slice(caretPos);
      if (newValue === val.slice(0, 2) + ':') {
        e.preventDefault();
        const hoursOnly = val.slice(0, 2);
        setTimeValue(hoursOnly);
        requestAnimationFrame(() => {
          try {
            target.setSelectionRange(2, 2);
          } catch {}
        });
      }
    }
  };

  // Обработка изменения даты
  const handleDateChange = (date: Dayjs | null, dateString: string) => {
    if (date && timeValue && validateTime(timeValue)) {
      // Есть валидное время - объединяем дату и время
      const [hours, minutes] = timeValue.split(':');
      const newDateTime = date.hour(parseInt(hours)).minute(parseInt(minutes));
      setDateValue(newDateTime);
      if (onChange) {
        onChange(newDateTime, newDateTime.format('YYYY-MM-DD HH:mm'));
      }
    } else if (date) {
      // Если нет валидного времени, устанавливаем только дату (без изменения поля времени)
      // Сохраняем время из текущего dateValue, если оно есть, иначе не передаем дату с временем
      if (dateValue && !dateValue.isSame(dateValue.startOf('day'))) {
        // Есть ранее установленное время - сохраняем его
        const dateWithTime = date.hour(dateValue.hour()).minute(dateValue.minute());
        setDateValue(dateWithTime);
        setTimeValue(dateWithTime.format('HH:mm'));
        setIsTimeEntered(true);
        if (onChange) {
          onChange(dateWithTime, dateWithTime.format('YYYY-MM-DD HH:mm'));
        }
      } else {
        // Нет валидного времени - устанавливаем только дату без времени
        const dateOnly = date.startOf('day');
        setDateValue(dateOnly);
        setTimeValue('');
        setIsTimeEntered(false);
        // Вызываем onChange с датой без времени, чтобы форма получила обновление
        // Валидация проверит, что время не введено
        if (onChange) {
          onChange(dateOnly, dateOnly.format('YYYY-MM-DD'));
        }
      }
    } else {
      setDateValue(null);
      setTimeValue('');
      setIsTimeEntered(false);
      if (onChange) {
        onChange(null, '');
      }
    }
  };

  // Обработка потери фокуса времени
  const handleTimeBlur = () => {
    // Если время полностью стерто, обновляем значение в форме
    if (!timeValue || timeValue.trim() === '') {
      setIsTimeEntered(false);
      // Если есть дата, передаем дату с началом дня, чтобы форма получила обновление
      // и валидация сработала
      if (dateValue) {
        const dateOnly = dateValue.startOf('day');
        setDateValue(dateOnly);
        if (onChange) {
          // Передаем дату с началом дня, чтобы форма получила обновление
          // Валидация проверит, что время не введено
          onChange(dateOnly, dateOnly.format('YYYY-MM-DD'));
        }
      }
      return;
    }
    
    // Если время невалидно, очищаем поле - не делаем автозаполнение
    if (!validateTime(timeValue)) {
      setTimeValue('');
      setIsTimeEntered(false);
      if (dateValue) {
        const dateOnly = dateValue.startOf('day');
        setDateValue(dateOnly);
        if (onChange) {
          onChange(dateOnly, dateOnly.format('YYYY-MM-DD'));
        }
      }
      return;
    }
    
    // Если время валидно, устанавливаем его в dateValue
    if (dateValue) {
      const [hours, minutes] = timeValue.split(':');
      const newDateTime = dateValue.hour(parseInt(hours)).minute(parseInt(minutes));
      if (!dateValue.isSame(newDateTime)) {
        setDateValue(newDateTime);
        setIsTimeEntered(true);
        if (onChange) {
          onChange(newDateTime, newDateTime.format('YYYY-MM-DD HH:mm'));
        }
      }
    }
  };

  return (
    <DateAndTimeContainer $containerHeight={containerHeight}>
      <DateAndTimeWrapper $dateOnFullRow={dateOnFullRow}>
        <DatePickerWrapper $dateOnFullRow={dateOnFullRow}>
          <CustomDatePicker
            {...props}
            value={displayDateValue}
            onChange={handleDateChange}
            format="YYYY-MM-DD"
            placeholder={placeholder}
            disabledDate={disabledDate}
            inputReadOnly={inputReadOnly}
            placement={placement}
            getPopupContainer={getPopupContainer}
            showTime={false}
            picker="date"
          />
        </DatePickerWrapper>
        {dateOnFullRow && <Spacer />}
        <TimeInputWrapper>
          <TimeInput
            value={timeValue}
            onChange={handleTimeChange}
            onFocus={() => setTimeValue('')}
            onKeyDown={handleTimeKeyDown}
            onBlur={handleTimeBlur}
            placeholder="00:00"
            maxLength={5}
          />
        </TimeInputWrapper>
      </DateAndTimeWrapper>
      {airportOffset !== undefined && airportOffset !== null && (
        <LocalTimeWrapper>
          <LocalTimeDisplay
            utcDate={dateValue}
            airportOffset={airportOffset}
          />
        </LocalTimeWrapper>
      )}
    </DateAndTimeContainer>
  );
};
